import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-icon.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { ApiRequest } from '../../../../interface/common.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchCpoTokenFilters,
  fetchCpoTokens,
  setSortData,
  updateCPOTokensFilters,
} from '../../../../redux/slices/OCPI/CPOTokensSlice';
import { RootState } from '../../../../redux/store';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';

// ── Types ─────────────────────────────────────────────────────────────────────

interface OcpiToken {
  uid: string;
  type?: string;
  auth_id?: string;
  visual_number?: string;
  issuer?: string;
  group_id?: string;
  valid?: boolean;
  whitelist?: string;
  language?: string;
  last_updated?: string;
  _objectId?: string;
  _lastUpdated?: string;
  emspCountryCode?: string;
  emspPartyId?: string;
  _createdAt?: string;
  createdAt?: string;
  created_at?: string;
  created?: string;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

/**
 * Renders a validity badge for a token.
 * @param {any} props The props.
 * @param {any} props.valid Whether the token is valid.
 */
function ValidBadge({ valid }: Readonly<{ valid: boolean }>) {
  const { t } = useTranslation(['ocpi']);
  return (
    <span
      className={`cpo-token-valid-badge ${
        valid ? 'cpo-token-valid-badge--yes' : 'cpo-token-valid-badge--no'
      }`}>
      {valid ? t('ocpi:valid') : t('ocpi:invalid')}
    </span>
  );
}

// ── Token Drill-Down Modal ─────────────────────────────────────────────────────

/**
 * Renders read-only details of a token received from a connected eMSP.
 * @param {any} props The props.
 * @param {any} props.token The token details.
 * @param {any} props.onClose The close callback.
 */
function TokenDetailsModal({
  token,
  onClose,
}: Readonly<{
  token: OcpiToken;
  onClose: () => void;
}>) {
  return (
    <ModalBox
      open
      header={false}
      notification
      title={null}
      closeModal={onClose}
      content={
        <div className="cpo-token-drilldown-modal">
          {/* Header */}
          <div className="cpo-drilldown-header">
            <div>
              <h3 className="cpo-drilldown-title">{token.uid}</h3>
              <p className="cpo-drilldown-subtitle">
                {token.valid !== undefined ? <ValidBadge valid={Boolean(token.valid)} /> : '–'}
                {token.emspCountryCode && token.emspPartyId
                  ? ` · Source: ${token.emspCountryCode} / ${token.emspPartyId}`
                  : ''}
              </p>
            </div>
            <button type="button" onClick={onClose} className="cpo-drilldown-close">
              <CloseIcon width={30} height={30} />
            </button>
          </div>

          {/* Raw token detail */}
          <div className="cpo-drilldown-body">
            <pre className="cpo-token-drilldown-detail">{JSON.stringify(token, null, 2)}</pre>
          </div>
        </div>
      }
    />
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

interface TokenRow {
  id: string;
  type: string;
  authId: string;
  visualNumber: string;
  issuer: string;
  emsp: string;
  valid: boolean;
  whitelist: string;
  language: string;
  lastUpdated: string;
  _createdAt?: string;
  createdAt: string;
  raw: OcpiToken;
}

/**
 * CpoTokensPanel component displays OCPI tokens received from connected eMSPs.
 * @param {any} props The props.
 * @param {any} props.countryCode The country code.
 * @param {any} props.partyId The party ID.
 * @param {any} props.filterCountryCode Optional country code of the eMSP to filter by.
 * @param {any} props.filterPartyId Optional party ID of the eMSP to filter by.
 * @param {any} props.refreshTrigger Optional number that triggers a reload when incremented.
 */
export default function CpoTokensPanel({
  countryCode,
  partyId,
  filterCountryCode,
  filterPartyId,
  refreshTrigger,
}: Readonly<{
  countryCode: string;
  partyId: string;
  filterCountryCode?: string;
  filterPartyId?: string;
  refreshTrigger?: number;
}>) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['ocpi']);
  const {
    tokens,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
    filters,
    filterListLoading,
  } = useAppSelector((state: RootState) => state.cpoTokens);
  const [selectedToken, setSelectedToken] = useState<OcpiToken | null>(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  /**
   * Fetches CPO tokens for this role with the given pagination/sort parameters.
   * @param {any} data - The pagination/sort parameters.
   */
  const fetchData = (data: ApiRequest) => {
    if (!loading) {
      dispatch(
        fetchCpoTokens({
          countryCode,
          partyId,
          counterPartyId: filterPartyId,
          counterCountryCode: filterCountryCode,
          ...data,
        }),
      );
    }
  };

  useEffect(() => {
    dispatch(
      fetchCpoTokens({
        countryCode,
        partyId,
        counterPartyId: filterPartyId,
        counterCountryCode: filterCountryCode,
      }),
    );
  }, [countryCode, partyId, filterPartyId, filterCountryCode, dispatch, refreshTrigger]);

  const columns = [
    {
      label: t('ocpi:created'),
      accessorKey: '_createdAt' as keyof TokenRow,
      sortAllow: false,
      /**
       * Rendering created date
       * @param {TokenRow} currentRow The current token row.
       * @returns {any} The rendered created date element.
       */
      customData: (currentRow: TokenRow) => (
        <span>{ConvertUTCtoDateTime(currentRow._createdAt || currentRow.createdAt)}</span>
      ),
    },
    {
      label: t('ocpi:tokenUid'),
      accessorKey: 'id' as keyof TokenRow,
      sortAllow: false,
      /**
       * Renders the token UID in a code badge.
       * @param {any} row The token row.
       */
      customData: (row: TokenRow) => (
        <code className="cpo-session-id-badge">{String(row.id).slice(0, 18)}</code>
      ),
    },
    {
      label: t('ocpi:sourceEmsp'),
      accessorKey: 'emsp' as keyof TokenRow,
      sortAllow: false,
      /**
       * Renders the source eMSP badge.
       * @param {any} row The token row.
       */
      customData: (row: TokenRow) => <span className="cpo-source-cpo-badge">{row.emsp}</span>,
    },
    {
      label: t('ocpi:type'),
      accessorKey: 'type' as keyof TokenRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:authId'),
      accessorKey: 'authId' as keyof TokenRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:visualNumber'),
      accessorKey: 'visualNumber' as keyof TokenRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:issuer'),
      accessorKey: 'issuer' as keyof TokenRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:valid'),
      accessorKey: 'valid' as keyof TokenRow,
      sortAllow: false,
      /**
       * Renders the validity badge.
       * @param {any} row The token row.
       */
      customData: (row: TokenRow) => <ValidBadge valid={row.valid} />,
    },
    {
      label: t('ocpi:whitelist'),
      accessorKey: 'whitelist' as keyof TokenRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:language'),
      accessorKey: 'language' as keyof TokenRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:created'),
      accessorKey: 'createdAt' as keyof TokenRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:lastUpdated'),
      accessorKey: 'lastUpdated' as keyof TokenRow,
      sortAllow: false,
    },
  ];

  const tableData: TokenRow[] = tokens.map((token: OcpiToken) => {
    const tokenUid = token.uid || token._objectId || '';
    const dateString = token.last_updated || token._lastUpdated;
    const lastUpdated = dateString ? new Date(dateString).toLocaleDateString() : '–';
    const sourceEmsp =
      token.emspCountryCode && token.emspPartyId
        ? `${token.emspCountryCode} / ${token.emspPartyId}`
        : '–';
    const createdAt =
      token._createdAt ||
      token.createdAt ||
      (token as any).created_at ||
      (token as any).created ||
      '';

    return {
      id: tokenUid,
      type: token.type || '–',
      authId: token.auth_id || '–',
      visualNumber: token.visual_number || '–',
      issuer: token.issuer || '–',
      emsp: sourceEmsp,
      valid: Boolean(token.valid),
      whitelist: token.whitelist || '–',
      language: token.language || '–',
      lastUpdated,
      _createdAt: createdAt,
      createdAt,
      raw: token,
    };
  });

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageAndLimitChange = (value: ApiRequest) => {
    fetchData({ ...value, sortField: sortField ?? undefined, sortBy: sortBy ?? undefined });
  };

  /**
   * Handle the table's sort change.
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: { sortField: string; sortBy: 'asc' | 'desc' }) => {
    dispatch(setSortData(sortData));
    fetchData({
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  return (
    <div className="cpo-tokens-panel">
      {/* ── Top bar ── */}
      <div className="cpo-tokens-topbar">
        <div>
          <h3 className="cpo-panel-title">{t('ocpi:receivedTokens')}</h3>
          <p className="cpo-panel-subtitle">
            {tokens.length > 0
              ? `${tokens.length} ${t(
                  tokens.length !== 1 ? 'ocpi:tokenCountPlural' : 'ocpi:tokenCountSingular',
                )} received from connected eMSPs`
              : t('ocpi:noTokensReceived')}
          </p>
        </div>
      </div>

      {/* ── Tokens Table ── */}
      <CustomTable
        version="v2"
        header={columns}
        data={tableData}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageAndLimitChange}
        filters={filters}
        filtersToDispatch={updateCPOTokensFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchCpoTokenFilters}
        sizeChangeButtonRequired
        actionDropdown
        rowClickEvent={(_id, row) => setSelectedToken(row.raw)}
        rowClickParamsKey="id"
        refershCurrentPages={handleRefresh}
        requiredRefreshToggle
        refreshFunction={handleRefresh}
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('ocpi:refresh')}
      />

      {/* ── Drill-down modal ── */}
      {selectedToken && (
        <TokenDetailsModal token={selectedToken} onClose={() => setSelectedToken(null)} />
      )}
    </div>
  );
}

CpoTokensPanel.defaultProps = {
  filterCountryCode: undefined,
  filterPartyId: undefined,
  refreshTrigger: undefined,
};
